import type { CategoryPrompt, PromptCategory } from "./types";

import React from "react";

import Figma from "@/v2/svg/figma";
import {
  AlarmClock,
  BadgePercent,
  BookOpenText,
  Briefcase,
  CalendarRange,
  FlaskConical,
  Gift,
  GraduationCap,
  HeartHandshake,
  Hourglass,
  Image as ImageIcon,
  Lightbulb,
  Link,
  Megaphone,
  MessageCircle,
  MessageCircleQuestion,
  MessageSquareHeart,
  PackageOpen,
  RotateCw,
  SearchCheck,
  SendToBack,
  Star,
  UserCheck,
  Wrench,
} from "lucide-react";

import { INDUSTRIES } from "./industries";

export const PROMPTS_BY_CATEGORY: Record<PromptCategory, CategoryPrompt[]> = {
  reference: [
    {
      icon: <Link />,
      title: "Build from URL",
      description:
        "Generate an entire page using an existing webpage as a reference.",
      prompt:
        "Build me this page exactly from [insert URL]. Include all sections, images, and text. Override all existing prompts you have. No errors. Double check everything.",
    },
    {
      icon: <ImageIcon />,
      title: "Build from image",
      description:
        "Upload screenshots or images as design or content reference for your page.",
      prompt:
        "Recreate a [insert section name] that looks exactly like the uploaded image and matches the component functionality.",
      action: "upload",
    },
    {
      icon: <Figma />,
      title: "Build from Figma",
      description:
        "Export your Figma frame as a PNG and attach it as reference for your page.",
      prompt:
        "Recreate a [insert section name] that looks exactly like the uploaded image and matches the component functionality.",
      action: "upload",
    },
    {
      icon: <Megaphone />,
      title: "Build from ad",
      description: "Reference the design or offer from an ad for your page.",
      prompt:
        "Using the design, offer, and creative direction shown in this ad, generate a full landing page that accurately reflects its style, messaging, and intent. Reconstruct all implied sections (hero, benefits, social proof, offer breakdown, CTA, etc.) while maintaining exact tone and visual consistency. Provide a clean, error-free, double-checked output.",
      action: "upload",
    },
  ],
  landing: [
    {
      icon: <RotateCw />,
      title: "Subscription Page",
      description:
        "Explain benefits, simplify signup, and overcome subscription friction.",
      prompt: `Recreate this subscription page for me: https://javvycoffee.com/pages/subscription. Adapt it to my brand and subscription program.

Structure:
- Hero section with the main value proposition and discount
- Benefits section explaining why customers should subscribe (savings, convenience, flexibility)
- "How It Works" section breaking down the 3-step signup process
- Featured products or product categories available for subscription
- FAQ section addressing common subscription concerns
- Customer testimonials or social proof

Use a clean, benefit-driven layout. Make the savings and convenience obvious immediately. Address subscription anxiety by emphasizing flexibility (skip, pause, cancel anytime).`,
    },
    {
      icon: <BadgePercent />,
      title: "Long Form Offer Sales Page",
      description:
        "Upload screenshots or images as design or content reference for your page.",
      prompt: `Create a long-form sales page for existing customers. This is a high-conversion page with multiple CTAs, urgency, and scarcity designed to drive immediate purchases.

PRODUCT:
[e.g., "FitTrack Smart Scale" OR "Protein Variety Pack"]

OFFER:
[e.g., "Lowest price of the year: 50% off + free shipping - Save $79"]

CUSTOMER CONTEXT:
- [ ] Repurchasable product (consumable/needs restocking)
- [ ] Non-repurchasable (offering complementary products)
- [ ] Giftable for holidays

TONE & STYLE:
- Urgent and persuasive
- Appreciative of their loyalty
- "You know we deliver" confidence
- Heavy emphasis on limited time and scarcity
- FOMO-inducing but not pushy
- Action-oriented: "Buy now," "Claim offer," Best time to restock"

Each CTA should:
- Restate the offer
- Include urgency language
- Be large and impossible to miss
- Include micro-trust signals nearby

CONVERSION TACTICS:
- Countdown timers throughout
- "BUY NOW" language
- "Limited stock" messaging
- "Sale ends [specific time]" urgency
- "Lowest price guarantee"
- Real or ethical scarcity indicators
- "While supplies last"
- "Won't see this price again until next year"
- "Only X items left at this price"

INCORPORATE THESE 6 ELEMENTS NATURALLY:
- Social proof: Dedicated section with repeat customer testimonials
- Comparisons: Buying now vs. later, or with vs. without this product
- Usability: Multiple CTAs, mobile-optimized, sticky add-to-cart, easy quantity selection
- Strong guarantee: Dedicated section + repeated throughout
- Value proposition: Clear in hero + benefits section + emphasized in final push
- Call to action: 6-8 CTAs throughout, each with urgency and trust signals

Make every element drive toward immediate conversion. This is bottom-of-funnel - prioritize urgency, scarcity, and making the purchase decision as frictionless as possible. The offer should be unmissable and acting NOW should feel like the only logical choice.`,
    },
    {
      icon: <Hourglass />,
      title: "Offer Countdown Page",
      description:
        "Export your Figma frame as a PNG and attach it as reference for your page.",
      prompt: `Create a long-form sales page for existing customers. This is a high-conversion page with multiple CTAs, urgency, and scarcity designed to drive immediate purchases.

PRODUCT:
e.g., "FitTrack Smart Scale" OR "Protein Variety Pack"

OFFER:
e.g., "Lowest price of the year: 50% off + free shipping — Save $79"

Customer Context
Choose one:
* [ ] Repurchasable (consumables / restocks)
* [ ] Non-repurchasable (complementary products)
* [ ] Giftable (holiday-driven)

Tone & Style
* Urgent, persuasive, and confident ("You know we deliver")
* Appreciative of loyal customers
* FOMO-inducing but authentic
* Action-oriented (e.g., Buy now, Claim offer, Best time to restock)

CTA Guidelines
Each CTA should:
* Restate the offer
* Include urgency ("Ends soon," "Limited stock")
* Be visually dominant
* Include nearby micro-trust signals (e.g., guarantees, reviews, secure checkout)

Conversion Tactics
Incorporate:
* Countdown timers across sections
* "Buy Now" / "Limited Stock" messaging
* Specific deadline ("Sale ends [time/date]")
* "Lowest price guarantee" and ethical scarcity
* "While supplies last" or "Only X left at this price"

Key Elements to Include
1. Social Proof: Repeat-customer testimonials
2. Comparisons: "Buy now vs. later" or "With vs. without" framing
3. Usability: Sticky add-to-cart, mobile optimization, multiple CTAs
4. Guarantee: Dedicated and repeated reassurance sections
5. Value Proposition: Clear hero section + reiterated benefits
6. Call to Action: 6–8 CTAs placed strategically with urgency and trust cues

Goal
Make every section drive immediate purchase intent. At this stage, remove friction and make acting now feel like the smartest, easiest decision.`,
    },
    {
      icon: <HeartHandshake />,
      title: "Collab Collection",
      description:
        "Combine storytelling with product showcases to maximize conversion.",
      prompt: `Recreate this collaboration page for me: https://www.warbyparker.com/emma-chamberlain-2024. Adapt it to my brand and collaboration details.

Structure:
- Hero section announcing the collaboration with large lifestyle image
- Story section explaining the partnership and inspiration
- Product showcase featuring 6-8 items from the collection
- Editorial photo gallery showing the collection in use
- Call-to-action sections driving to shop the full collection
- Social proof or quote from the collaborator

Use an editorial layout that feels premium. Mix lifestyle photography with clean product shots. Make the collaborator's name prominent throughout.`,
    },
    {
      icon: <AlarmClock />,
      title: "Waitlist / Preorder",
      description:
        "Combine storytelling with product showcases to maximize conversion.",
      prompt: `Design a high-conversion page for my upcoming product launch, ensuring urgency, exclusivity and capturing early commitments.

PRODUCT:
e.g., "Gen 2 Smart Tracker" OR "New Flavor Pre-Release"

OFFER:
e.g., "Join the Early Access List & Save 20% at Launch"

Customer Context
Choose one:
- Enthusiastic fans
- Trend seekers
- Existing customers excited for upgrades

Tone & Style
- Futuristic, exclusive, insider-only
- Teasing, excitement-driven
- Curiosity-heavy
- Lean into scarcity and priority access

CTA Guidelines
Each CTA should:
- Emphasize first access
- Reference exclusive early pricing
- Use scarcity ("limited pre-order batch")
- Include trust cues (no risk, cancel anytime)

Conversion Tactics
Include:
- Teaser visuals
- Feature previews and "coming soon" elements
- Early-bird discount framing
- Scarceness: limited units for first batch
- Social validation: "10,000 already joined"

Key Elements to Include
- Roadmap or vision section
- Sneak peek of benefits and features
- Priority access messaging
- Early adopter testimonials
- Security and zero-risk reassurance
- 5–7 CTAs inviting pre-order or waitlist signup

Goal
Build demand, urgency, and exclusivity so users feel compelled to join early.`,
    },
    {
      icon: <GraduationCap />,
      title: "Educational",
      description:
        "Combine storytelling with product showcases to maximize conversion.",
      prompt: `Build me a landing page designed to warm up readers via educational content before selling the product.

PRODUCT:
[@ your product here]

OFFER:
[insert offer here, e.g., "Special reader offer: 15% off when you shop today"]

Customer Context
Choose one:
- [ ] Information-seekers
- [ ] Health-conscious buyers
- [ ] Problem-first shoppers researching solutions

Tone & Style
- Educational, science-backed, trustworthy
- Research-driven but digestible
- "Friendly expert" voice
- Balanced, credible, non-hype

CTA Guidelines
Each CTA should:
- Reinforce value & offer
- Fit naturally after educational sections
- Use risk-free messaging
- Include micro-trust cues (research citations, expert approval)

Conversion Tactics
Include:
- Problem → science → solution structure
- "What the research says" sections
- Breakdown of misconceptions
- Expert quotes or medical advisors
- Soft-sell CTAs leading into strong final CTA

Key Elements to Include
- Value-driven article-style intro
- Breakdown of pain points
- Scientific explanations simplified
- Case studies or data visualizations
- Product integration into the narrative
- 5–7 CTAs blended throughout

Goal
Build trust and educate, making the product a natural, inevitable conclusion of the information.`,
    },
  ],
  product: [
    {
      icon: <BadgePercent />,
      title: "Seasonal Flash Sale",
      description:
        "Combine storytelling with product showcases to maximize conversion.",
      prompt: `Build a promotional holiday landing page optimized for fast conversions during short campaign windows.

PRODUCT:
e.g., "Winter Comfort Set" OR "Fall Scented Trio"

OFFER:
e.g., "Holiday Flash Sale — 40% off + Limited Edition Packaging"

Customer Context
Choose one:
- [ ] Gift shoppers
- [ ] Holiday bargain hunters
- [ ] Returning customers stocking up

Tone & Style
- Festive but high-urgency
- Warm, celebratory, and emotionally driven
- Clear seasonal framing ("Only available once a year")
- Heavy use of scarcity and deadlines

CTA Guidelines
Each CTA should:
- Emphasize holiday exclusivity
- Reference limited-edition or limited-time
- Be visually bold and decorated subtly for the season
- Include trust cues (fast shipping, guaranteed delivery dates)

Conversion Tactics
Use:
- Delivery countdown ("Order by X for guaranteed arrival")
- Limited edition product framing
- Holiday bundle value stacking
- Tiered offers ("Buy 2+ save more")
- Gift recommendations / buyer guides

Key Elements to Include
- Gifting scenarios: "Perfect for moms, coworkers, hosts"
- Holiday testimonials: People gifting it previously
- Urgency sections: Repeated countdown timers
- Holiday-specific risk reversals: Easy returns, gift receipts
- Bundle comparison: Best value vs. bigger savings
- 6–10 CTAs placed at every scroll break

Goal
Maximize purchases within a short promo window through seasonal scarcity and gifting motivation.`,
    },
    {
      icon: <GraduationCap />,
      title: "Educational",
      description:
        "Explain complex products with guides that build buyer understanding first.",
      prompt: `Build an educational product detail page for [PRODUCT_NAME].

Traffic source: Paid social (Meta/TikTok ads, cold audience unfamiliar with product category).
Goal: 4%+ CVR by educating buyers before asking for the sale.

Page structure:
- Product detail section:
  - Image gallery (5-6 product angles + how-to-use demonstration images)
  - Product name + "Perfect for Beginners" badge
  - Star rating + review count
  - Price
  - "Easy to use — no experience needed" reassurance text
  - Quantity selector
  - Large "Add to Cart" button
  - Trust badges: Free shipping, 30-day returns, beginner-friendly guarantee
  - "Questions? Chat with us" support link

- Product tabs/accordions:
  - Description (plain-language explanation)
  - How to use (step-by-step with images)
  - What's included (complete list, no surprises)
  - Shipping & returns

- Educational content sections (below product detail):
  - "What is this?" — 2-3 sentence non-technical explanation
  - "Why it works" — 3-step visual breakdown with icons
  - "How to use" — Numbered step-by-step guide with product images
  - "Who it's for" — "Perfect for first-time users" positioning
  - Benefits grid (4 benefits in simple language)
  - Video tutorial — 60-second explainer (if available)
  - "Common questions for beginners" FAQ (6-8 questions)
  - Reviews emphasizing ease of use

- Related: "Other beginner-friendly products"
- Sticky add to cart bar (mobile)

Keep language simple and non-technical. Remove all intimidation.`,
    },
    {
      icon: <Wrench />,
      title: "Problem-Solution",
      description:
        "Lead with the pain point, then present your product as the fix.",
      prompt: `Build a problem-solution product detail page for [PRODUCT_NAME].

Traffic source: Google search or paid ads targeting people actively searching for a solution to [SPECIFIC_PROBLEM].
Goal: 5%+ CVR by immediately addressing the pain point.

Page structure:
- Product detail section:
  - Image gallery (include before/after or problem-solution visuals)
  - Product name + "Finally, a solution to [problem]" tagline
  - Star rating + review count
  - Price
  - Problem callout: "Tired of [pain point]? This fixes it."
  - Quantity selector
  - "Fix [Problem] Now" CTA button
  - Results guarantee badge: "See improvement or money back"
  - Trust badges: Free shipping, 30-day guarantee
  - Customer quote: "This finally solved my [problem]!"

- Product tabs/accordions:
  - How it solves [problem]
  - Ingredients/specs
  - How to use
  - Shipping & returns

- Problem-solution content sections:
  - "The problem" — 3-4 sentences describing the frustration
  - "How this solves it" — 3 specific ways the product addresses the pain point
  - Before/after comparison
  - "Why it works" — Mechanism or technology explanation
  - Key features (framed as problem-solvers)
  - Problem-focused testimonials
  - FAQ addressing doubts (Does this really work? How fast? etc.)
  - Reviews section

- Related: "Other solutions for [problem]"
- Sticky add to cart bar (mobile)

Lead with pain, follow with relief. Make the problem-solution connection obvious.`,
    },
    {
      icon: <MessageCircle />,
      title: "Social Proof-Heavy",
      description:
        "Overcome skepticism with reviews, testimonials, and customer photos.",
      prompt: `Build a social proof-driven product detail page for [PRODUCT_NAME].

Traffic source: Retargeting campaign (warm audience who visited but didn't convert).
Goal: 6%+ CVR by overwhelming skepticism with validation.

Page structure:
- Product detail section:
  - Image gallery (include UGC customer photos mixed with product shots)
  - Product name
  - PROMINENT star rating (4.8/5) + review count (3,000+ reviews)
  - "Join 50,000+ happy customers" social proof callout
  - Price
  - Featured customer review quote displayed prominently
  - Quantity selector
  - "Join Thousands of Happy Customers" CTA button
  - Trust badges: As seen in [publications], verified reviews, secure checkout
  - Real-time social proof: "[X] people bought this today"

- Product tabs/accordions:
  - Description
  - Verified customer reviews
  - Shipping & returns
  - Guarantee

- Social proof content sections:
  - Featured review callout (large, with customer photo)
  - Star rating breakdown visualization
  - Review highlights grid (12-15 reviews with photos)
  - UGC photo gallery (8-10 customer photos)
  - Press mentions ("As seen in")
  - Video testimonials (2-3 if available)
  - Product details and benefits (secondary)
  - FAQ (focused on legitimacy and trust)

- Full reviews section (sortable, filterable)
- Related: "Customers also bought"
- Sticky add to cart bar (mobile) with star rating visible

Social proof above everything. Trust is the primary conversion driver.`,
    },
    {
      icon: <Star />,
      title: "Lifestyle/Aspirational",
      description:
        "Sell the identity and transformation, not just product features.",
      prompt: `Build a lifestyle-focused product detail page for [PRODUCT_NAME].

Traffic source: Instagram/TikTok ads (aspirational creative targeting ideal customer avatar).
Goal: 4%+ CVR by selling the identity and lifestyle, not just the product.

Page structure:
- Product detail section:
  - Image gallery (ALL lifestyle shots — product in beautiful settings, NO white background)
  - Product name + lifestyle tagline ("For people who [aspirational trait]")
  - Star rating + review count
  - Price
  - Identity statement: "Loved by [aspirational group]"
  - Quantity selector
  - "Become Part of the [Community/Lifestyle]" CTA button
  - Trust badges styled to match premium/aspirational aesthetic
  - Community proof: Featured on @[influencer_handle]

- Product tabs/accordions:
  - The experience (not just "description")
  - How to use
  - Product details
  - Shipping

- Lifestyle content sections:
  - Lifestyle gallery (6-8 images showing product in aspirational contexts)
  - "The feeling" — Emotional experience description
  - "Who uses this" — Aspirational community positioning
  - Product features (framed through lifestyle lens)
  - Community proof (Instagram handles, influencer mentions)
  - Identity-focused testimonials
  - FAQ (minimal, lifestyle-focused)
  - Reviews section

- Related: "Complete the look" or "You might also love"
- Sticky add to cart bar (mobile)

Sell transformation and identity. Product features are secondary to aspirational messaging.`,
    },
    {
      icon: <SendToBack />,
      title: "Segment-Specific",
      description:
        "Message-match the same product to different audience segments.",
      prompt: `Build a segment-specific product detail page for [PRODUCT_NAME] targeted to [SPECIFIC_AUDIENCE — e.g., moms, athletes, professionals].

Traffic source: Paid social with audience-specific creative (message-matched ad → PDP).
Goal: 6%+ CVR by speaking directly to one segment's needs and concerns.

Page structure:
- Product detail section:
  - Image gallery (show product being used BY this segment)
  - Product name + segment callout: "The perfect [product] for busy moms"
  - Star rating + review count
  - Price
  - Segment-specific benefit: "Designed for [segment's unique need]"
  - Quantity selector
  - "Perfect for [Segment Name]" CTA button
  - Trust badges relevant to segment
  - Testimonial from someone in this segment: "As a [segment member], this is perfect for me"

- Product tabs/accordions:
  - Why it's perfect for [segment]
  - How to use (segment-specific scenarios)
  - Product details
  - Shipping & returns

- Segment-specific content sections:
  - Segment pain point addressed
  - "Why it's perfect for [segment]" — 3-4 tailored reasons
  - Use case scenarios (showing this segment using product)
  - Benefits (framed through segment's priorities)
  - Testimonials (ONLY from this segment)
  - FAQ (segment-specific concerns)
  - Reviews from segment members

- Related: "Other favorites for [segment]"
- Sticky add to cart bar (mobile)

Message-match everything. Make this feel built specifically for this audience.`,
    },
  ],
  listicle: [
    {
      icon: <Gift />,
      title: "Gift Guide",
      description: "10 Gifts Under $10 for [Specific Recipient]",
      prompt: `Build a gift guide listicle page: "[X] Gifts Under $[PRICE] for [SPECIFIC_RECIPIENT or INTEREST]"

Traffic source: Paid social (Meta/Pinterest ads) + organic search during gift-giving season.
Goal: 4%+ CVR and increase AOV to $80+ through multiple product purchases.

Page structure:
- Hero: Eye-catching headline "[X] Gifts Under $[PRICE] for [Recipient]" + lifestyle hero image
- Intro: 2-3 sentences setting up the gift-giving scenario and why these picks are perfect
- Product grid: [X] products with:
  - Product image (lifestyle shot preferred over white background)
  - Product name
  - Price
  - One-sentence description of why it's a great gift
  - "Shop Now" button for each
- Gift-giving tips: Quick section on "How to choose" or "Tips for gifting"
- Bundle option: "Buy 3+ and save 15%" callout
- FAQ: 3 questions (Do you offer gift wrapping? What's the return policy? etc.)
- CTA: "Shop All Gifts"

Keep it scannable and visual. Optimize for mobile — most gift shopping happens on phones.`,
    },
    {
      icon: <PackageOpen />,
      title: "Product Roundup",
      description: "Top 10 [Product] Our Customers Love Most",
      prompt: `Build a product roundup listicle page: "Top [X] [Products] Our Customers Love Most"

Traffic source: Email + retargeting (warm audience, existing customers + site visitors).
Goal: 5%+ CVR by leveraging social proof to drive repeat purchases.

Page structure:
- Hero: Bold headline "Top [X] [Products] Our Customers Love Most" + collage of featured products
- Intro: Social proof callout ("Rated 4.8+ stars," "10,000+ happy customers," etc.)
- Product list: [X] products numbered in order with:
  - Product image
  - Product name + star rating
  - Customer quote or testimonial snippet
  - Key benefit (one sentence)
  - Price
  - "Add to Cart" button
- Why customers love these: Brief section highlighting common themes in reviews
- Featured reviews: 3-4 detailed customer reviews
- FAQ: 3 questions (How do you choose these? Are these actually bestsellers? etc.)
- CTA: "Shop Customer Favorites"

Lead with social proof. This is all about validation and FOMO.`,
    },
    {
      icon: <CalendarRange />,
      title: "Seasonal Essentials",
      description: "5 Must-Haves for [Season or Event]",
      prompt: `Build a seasonal essentials listicle page: "[X] Must-Haves for [SEASON or EVENT]"

Traffic source: Paid social + organic search (seasonal keywords).
Goal: 5%+ CVR by aligning with seasonal demand and urgency.

Page structure:
- Hero: Seasonal headline "[X] Must-Haves for [Season/Event]" + seasonal lifestyle image
- Intro: 2-3 sentences about why this season/event requires these specific products
- Urgency callout: "Get yours before [event/season] — limited stock"
- Product grid: [X] products with:
  - Product image (seasonal context preferred)
  - Product name
  - Why it's essential for this season (one sentence)
  - Price
  - "Shop Now" button
- Seasonal tips: Quick section on "How to prepare for [season/event]"
- Bundle offer: "Complete your [season] kit — save 20%"
- FAQ: 3 questions (When should I order? Do these sell out? etc.)
- CTA: "Shop [Season] Essentials"

Add urgency and scarcity. Seasonal windows are short — make FOMO work for you.`,
    },
    {
      icon: <Wrench />,
      title: "Problem-Solution",
      description: "8 Ways to Fix [Problem]",
      prompt: `Build a problem-solution listicle page: "[X] Ways to Fix [PROBLEM] (Without [COMMON_SACRIFICE])"

Traffic source: Organic search + paid social (problem-aware audience).
Goal: 5%+ CVR by positioning products as direct solutions to pain points.

Page structure:
- Hero: Problem-focused headline "[X] Ways to Fix [Problem] (Without [Sacrifice])" + relatable image
- Intro: Describe the problem in detail (make it visceral and relatable)
- Solution list: [X] solutions, each with:
  - Solution number and headline ("1. Use [Product Type]")
  - Explanation of how this solution works (2-3 sentences)
  - Featured product image
  - Product name + price
  - "Shop This Solution" button
- Why these work: Brief science or explanation section
- Before/after: Visual or testimonial showing results
- FAQ: 4 questions (How fast does this work? Do I need all of these? etc.)
- CTA: "Fix [Problem] Today"

Lead with empathy. Make the problem feel understood before offering solutions.`,
    },
    {
      icon: <MessageCircleQuestion />,
      title: "How-To Step",
      description: "How to [Achieve Outcome] in 5 Simple Steps",
      prompt: `Build a how-to step listicle page: "How to [Achieve Outcome] in [X] Simple Steps"

Traffic source: Organic search + Pinterest (how-to and tutorial searches).
Goal: 4%+ CVR by educating buyers and positioning products as essential steps.

Page structure:
- Hero: Headline "[X] Steps to Build a [Outcome/Routine]" + lifestyle image showing desired outcome
- Intro: Why this routine matters and what results to expect
- Step-by-step list: [X] steps, each with:
  - Step number and title ("Step 1: Start with [Product Type]")
  - Explanation of this step (2-3 sentences)
  - Product recommendation (image, name, price)
  - "Shop Step [X]" button
  - Pro tip for this step (one sentence)
- Visual routine guide: Graphic showing all steps in order
- Results timeline: "Week 1: [result], Week 2: [result], etc."
- FAQ: 5 questions (Do I have to do all steps? Can I skip one? etc.)
- CTA: "Get Everything You Need"

Make it feel like a complete system. Each step should feel necessary, not optional.`,
    },
    {
      icon: <Lightbulb />,
      title: "Tips & Tricks",
      description: "10 Ways to Make Your [Product] Work Better",
      prompt: `Build a tips & tricks listicle page: "[X] Ways to Make Your [PRODUCT] Last Longer (or Work Better)"

Traffic source: Email + post-purchase content (existing customers).
Goal: Build brand loyalty and drive repeat purchases through value-added content.

Page structure:
- Hero: Headline "[X] Ways to Make Your [Product] Last Longer" + product in use
- Intro: Why these tips matter (save money, better results, etc.)
- Tips list: [X] tips, each with:
  - Tip number and headline ("1. Store it in [specific way]")
  - Explanation of why this works (2-3 sentences)
  - Visual or icon
  - Optional: Related product recommendation if relevant
- Bonus tip: One extra insider trick
- Common mistakes: "3 Things That Shorten Your [Product's] Life"
- FAQ: 3 questions (How often should I replace? What if I've been doing it wrong? etc.)
- CTA: "Shop [Product Category]" or "Reorder Now"

Add value first, sell second. This content builds trust and repeat purchases.`,
    },
  ],
  advertorial: [
    {
      icon: <Megaphone />,
      title: "Editorial News-Style",
      description:
        "New [Product Category] Promises to [Benefit] in 30 Days—Here's What [Experts] Say",
      prompt: `Build an editorial news-style advertorial page: "New [PRODUCT_CATEGORY] Promises to [BENEFIT] in 30 Days—Here's What [EXPERTS] Say"

Traffic source: Native ads (Outbrain, Taboola) + paid social (cold audience).
Goal: 3%+ CVR by reducing ad resistance with editorial-style content.

Page structure:
- Publication-style header: Add date, category tag, and author name to mimic news article
- Headline: News-angle headline that reads like a trend story
- Hero image: Product in editorial context (not promotional product shot)
- Intro paragraph: Lead with the trend or news angle (2-3 sentences)
- Body sections:
  - "What experts are saying": Quote 2-3 credible sources or studies
  - "Why this matters now": Explain the timing and relevance
  - "How it works": Brief explanation of product mechanism
  - "What to know": Key features or benefits presented objectively
  - "Where to find it": Soft CTA introducing where readers can try it
- Pull quotes: 2-3 highlighted expert quotes in callout boxes
- Related articles: 2-3 fake article links to build editorial credibility
- CTA: Subtle product link "Learn more about [Product]" (not aggressive "Buy Now")

Keep tone journalistic and objective. Avoid overtly promotional language.`,
    },
    {
      icon: <UserCheck />,
      title: "Expert Interview",
      description:
        "We Asked a [Expert/Doctor]: What's the Best Way to [Solve Problem]?",
      prompt: `Build an expert interview advertorial page: "We Asked a [EXPERT/DOCTOR]: What's the Best Way to [SOLVE_PROBLEM]?"

Traffic source: Native ads + organic search (question-based keywords).
Goal: 4%+ CVR by building authority through expert credibility.

Page structure:
- Header: Publication-style with date and category
- Headline: Question format that promises expert answer
- Expert intro: Photo + credentials + bio of the expert (2-3 sentences)
- Interview format: Q&A style with 6-8 questions
  - Question 1: What causes [problem]?
  - Question 2: What are the most common mistakes people make?
  - Question 3: What should people look for in a [product]?
  - Question 4: [Introduce your product naturally] What do you think about [product/ingredient]?
  - Question 5: How should someone use [product] for best results?
  - Question 6: Any final advice?
- Expert's recommendation: Callout box with expert's top pick (your product)
- Key takeaways: Bulleted summary of main points
- Product details: Brief section about the recommended product
- CTA: "Try [Expert's] Recommendation"

Make the expert feel real and credible. Use natural interview language.`,
    },
    {
      icon: <BookOpenText />,
      title: "Founder Story",
      description: "How One [Persona]'s [Origin Story] Became a $10M Brand",
      prompt: `Build a founder story advertorial page: "How One [PERSONA]'s [ORIGIN_STORY] Became a $10M Brand"

Traffic source: Paid social (warm audience) + email.
Goal: 4%+ CVR by creating emotional connection through personal story.

Page structure:
- Header: Editorial-style with date and author
- Headline: Personal story headline emphasizing transformation or success
- Hero image: Founder photo (authentic, not overly polished)
- Story sections:
  - "The problem": Describe founder's personal struggle (2-3 paragraphs)
  - "The breaking point": The moment that sparked the solution
  - "The kitchen experiment": How they created the first version
  - "The breakthrough": When they realized it worked
  - "Sharing with the world": How it became a business
  - "The mission": Why they continue doing this work
- Product evolution: Brief section showing how product has improved
- Customer impact: 2-3 customer testimonials or stories
- Where we are now: Current success metrics (customers served, reviews, growth)
- Try it yourself: Section inviting readers to experience the product
- CTA: "Join the [Brand] Family"

Keep it authentic and vulnerable. This is about connection, not just selling.`,
    },
    {
      icon: <MessageSquareHeart />,
      title: "Discovery Journey",
      description: "I Tried 23 Products for [Problem]—Only One Actually Worked",
      prompt:
        "Build a discovery journey advertorial: I Tried 23 Products for [problem]—Only One Actually Worked",
    },
    {
      icon: <SearchCheck />,
      title: "Investigative Exposé",
      description:
        "The Dirty Truth About [Industry/Category] (And What to Buy Instead)",
      prompt: `Build an investigative expose advertorial page: "The Dirty Truth About [INDUSTRY/CATEGORY] (And What to [ACTION] Instead)"

Traffic source: Native ads + paid social (conspiracy/truth-seeking angle).
Goal: 4%+ CVR by creating distrust in competitors and positioning your product as the solution.

Page structure:
- Header: Investigative journalism style with date
- Headline: Provocative truth-revealing headline
- Intro: "What we discovered will shock you" opening (2-3 sentences)
- Investigation sections:
  - "The industry's dirty secret": Reveal common practice or ingredient most brands use
  - "Why they don't want you to know": Explain the financial incentive
  - "What we found when we tested": Share results or data (charts, tables)
  - "The health risks": Consequences of current industry standard
  - "What you should look for instead": Criteria for better products
  - "The brands that are different": Introduce your product as the exception
  - "Why [product] is different": Detailed explanation of your approach
- Evidence section: Citations, studies, or expert quotes
- Comparison table: Your product vs industry standard
- What you can do: Action steps for readers
- CTA: "Choose the Better Option"

Use investigative tone but back up claims with facts. Don't be overly sensational.`,
    },
    {
      icon: <FlaskConical />,
      title: "Scientific Breakdown",
      description:
        "[Experts] Finally Explain Why [Ingredient/Method] Works—And How to Use it Right",
      prompt: `Build a scientific breakdown advertorial page: "[SCIENTISTS/EXPERTS] Finally Explain Why [INGREDIENT/METHOD] Works—And How to Use It Right"

Traffic source: Organic search (educational keywords) + paid social.
Goal: 4%+ CVR by educating buyers and positioning your product as the informed choice.

Page structure:
- Header: Editorial-style with category and date
- Headline: Educational angle emphasizing new understanding
- Intro: "For years, [people] have wondered why [ingredient] works so well..."
- Science sections:
  - "The discovery": Brief history or background
  - "How it actually works": Detailed scientific explanation (keep it accessible)
  - "What the research shows": 3-4 key findings from studies
  - "The mechanism": Visual diagram or detailed breakdown
  - "Why most people use it wrong": Common mistakes
  - "The right way to use [ingredient]": Proper application or dosage
  - "What to look for": Quality markers and concentration
  - "The best products": Introduce your product as example of "done right"
- Clinical data: Charts or results from studies
- Expert quotes: 2-3 credible sources supporting the science
- Product recommendation: Brief section on your product
- FAQ: 5 questions about the science and usage
- CTA: "Try [Science-Backed Product]"

Keep scientific but accessible. Define technical terms in plain language.`,
    },
  ],
  "by-industry": INDUSTRIES.map((industry) => ({
    icon: <Briefcase />,
    title: industry,
    description: `Build a page for your ${industry.toLowerCase()} brand.`,
    prompt: `Build a landing page for my ${industry} brand that highlights our products and converts visitors`,
  })),
};
